<script setup>
import { computed } from 'vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisEntitySummaryCard — the aside/detail summary for any LAIS entity
 * (imóvel, pessoa, proprietário, proposta, run, contrato). Purely
 * presentational: the domain passes a title, optional subtitle/avatar, a set of
 * status badges, redaction-aware metadata rows and secondary actions (slot).
 *
 * Sensitive metadata rows are masked when `row.redacted` (or the global
 * `redacted` prop) is set — the real value is never placed in the DOM.
 */
const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  href: { type: String, default: '' },
  icon: { type: String, default: '' },
  avatarUrl: { type: String, default: '' },
  // [{ label, tone, icon }]
  badges: { type: Array, default: () => [] },
  // [{ label, value, redacted }]
  meta: { type: Array, default: () => [] },
  redacted: { type: Boolean, default: false },
  mask: { type: String, default: '••••••' },
  emptyLabel: { type: String, default: '—' },
  selectable: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
});

const emit = defineEmits(['select']);

const rootTag = computed(() => {
  if (props.href) return 'a';
  if (props.selectable) return 'button';
  return 'article';
});
const hasHeaderMedia = computed(() => Boolean(props.avatarUrl || props.icon));
const visibleMeta = computed(() =>
  props.meta.filter(row => row && (row.label || row.value))
);
const isRowRedacted = row => Boolean(props.redacted || row.redacted);
const rowValue = row => {
  if (isRowRedacted(row)) return props.mask;
  const value = row.value;
  return value === null || value === undefined || value === ''
    ? props.emptyLabel
    : String(value);
};

const handleActivate = event => {
  if (props.href) return;
  if (props.selectable) emit('select', event);
};
</script>

<template>
  <component
    :is="rootTag"
    :href="rootTag === 'a' ? href : undefined"
    :type="rootTag === 'button' ? 'button' : undefined"
    :aria-label="ariaLabel || title || undefined"
    class="flex w-full flex-col gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-left shadow-lais-card"
    :class="
      href || selectable
        ? 'cursor-pointer transition hover:border-lais-primary/50 hover:shadow-lais-pop focus-visible:outline-none focus-visible:shadow-lais-focus'
        : ''
    "
    @click="handleActivate"
  >
    <div class="flex min-w-0 items-start gap-3">
      <span
        v-if="hasHeaderMedia"
        class="grid size-11 shrink-0 place-items-center overflow-hidden rounded-lais-circle bg-lais-primary-50 text-lais-primary"
      >
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="title"
          class="size-full object-cover"
        />
        <span v-else class="size-5" :class="icon" aria-hidden="true" />
      </span>
      <div class="min-w-0 flex-1">
        <h3
          v-if="title"
          class="truncate font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ title }}
        </h3>
        <p
          v-if="subtitle"
          class="mt-0.5 truncate text-lais-body-sm text-lais-text-muted"
        >
          {{ subtitle }}
        </p>
      </div>
      <span
        v-if="href"
        class="i-lucide-chevron-right size-4 shrink-0 text-lais-text-muted"
        aria-hidden="true"
      />
    </div>

    <div v-if="badges.length" class="flex flex-wrap gap-1.5">
      <LaisStatusBadge
        v-for="(badge, index) in badges"
        :key="`${badge.label}-${index}`"
        :label="badge.label"
        :tone="badge.tone"
        :icon="badge.icon"
        size="sm"
      />
    </div>

    <dl v-if="visibleMeta.length" class="grid gap-2">
      <div
        v-for="(row, index) in visibleMeta"
        :key="`${row.label}-${index}`"
        class="flex items-baseline justify-between gap-3"
      >
        <dt class="shrink-0 text-lais-label-xs uppercase text-lais-text-muted">
          {{ row.label }}
        </dt>
        <dd
          class="min-w-0 truncate text-right text-lais-body-sm font-medium text-lais-text"
          :class="isRowRedacted(row) ? 'select-none tracking-wider' : ''"
          :aria-label="isRowRedacted(row) ? 'Valor protegido' : undefined"
        >
          {{ rowValue(row) }}
        </dd>
      </div>
    </dl>

    <div v-if="$slots.actions" class="flex flex-wrap gap-2 pt-1">
      <slot name="actions" />
    </div>
    <slot />
  </component>
</template>
